/**
 * Single source of truth for everything that varies by business vertical.
 *
 * DataPanel, PlanView, and StageProgress are thin renderers that read from
 * VERTICAL_CONFIG — they contain NO `if (vertical === ...)` branching. Add a
 * vertical or move a field in ONE place here and every view stays in sync.
 *
 * Per vertical:
 *   stages            — ordered stage ids for StageProgress (the PRE-LAUNCH
 *                       flow). Mirrors the backend schema STAGES, with the
 *                       synthetic `classify` opener prepended and the F3
 *                       business-plan stages appended. F5 operating-business
 *                       stages (actuals_upload, actuals_confirm, loan_purpose,
 *                       capital_expenses) are NOT listed here — they are
 *                       inserted per-session by the backend `active_stages`
 *                       the stepper reads, so pre-launch sessions are unchanged.
 *   startupLineItems  — rows for the PlanView "Startup Summary" table.
 *                       { key, label, source, rowClass? }
 *                         source 'plan'      → plan.startup_costs[key]
 *                         source 'collected' → collected_inputs[key].value
 *                       (services/retail itemise their capital from collected
 *                        inputs because the engine lumps them into the
 *                        restaurant-shaped startup_costs output.)
 *   keyMetrics        — the vertical-VARYING financial rows for the PlanView
 *                       Key Metrics cards + DataPanel KeyFinancials.
 *                       { key, label, format, source }
 *                         source 'breakeven' → plan.break_even[key]
 *                         source 'collected' → collected_inputs[key].value
 *                       format ∈ currency | integer | percent | bigpercent
 *   dataPanelSections — the DataPanel "Your Plan So Far" sections.
 *                       [{ section, fields: [{ name, label, format, alwaysShow? }] }]
 *                       format names are the ones DataPanel.formatField knows.
 */

// Field names are validated against the backend schemas in
// schemas/industries/{restaurant,services,retail}.py — every `key`/`name`
// here is a real collected-input or plan-output field.

const RESTAURANT = {
  stages: [
    'classify', 'concept', 'location', 'revenue', 'costs', 'opex',
    'capital', 'financing', 'ar_timing', 'growth',
    'bp_collect', 'pfs_collect', 'bp_generate', 'bp_complete',
  ],
  startupLineItems: [
    { key: 'kitchen_buildout_cost', label: 'Kitchen Buildout', source: 'plan' },
    { key: 'equipment_cost', label: 'Equipment', source: 'plan' },
    { key: 'hard_costs_subtotal', label: 'Hard Costs Subtotal', source: 'plan', rowClass: 'subtotal' },
    { key: 'contingency_buffer', label: 'Contingency Buffer (10%)', source: 'plan' },
    { key: 'preopening_costs', label: 'Pre-Opening Costs', source: 'plan' },
    { key: 'initial_inventory', label: 'Initial Inventory', source: 'plan' },
    { key: 'working_capital_reserve', label: 'Working Capital Reserve', source: 'plan' },
    { key: 'total_capital_required', label: 'Total Capital Required', source: 'plan', rowClass: 'total' },
  ],
  keyMetrics: [
    { key: 'break_even_revenue_operating', label: 'Break-Even Revenue (Operating)', format: 'currency', source: 'breakeven' },
    { key: 'break_even_revenue_with_debt_service', label: 'Break-Even Revenue (Incl. Debt Service)', format: 'currency', source: 'breakeven' },
    { key: 'break_even_covers_daily', label: 'Break-Even Covers/Day', format: 'integer', source: 'breakeven' },
    { key: 'break_even_occupancy_pct', label: 'Break-Even Occupancy', format: 'bigpercent', source: 'breakeven' },
  ],
  dataPanelSections: [
    {
      section: 'Business Concept',
      fields: [
        { name: 'business_stage', label: 'Business Stage', format: 'stage' },
        { name: 'years_in_operation', label: 'Years in Operation', format: 'int' },
        { name: 'business_name', label: 'Name', format: 'text' },
        { name: 'service_model', label: 'Type', format: 'text' },
        { name: 'cuisine_type', label: 'Cuisine', format: 'text' },
        { name: 'legal_structure', label: 'Legal Structure', format: 'text' },
      ],
    },
    {
      section: 'Location',
      fields: [
        { name: 'location_city', label: 'City', format: 'text' },
        { name: 'rent', label: 'Monthly Rent', format: 'currency_per_month' },
        { name: 'square_footage', label: 'Space', format: 'sqft' },
      ],
    },
    {
      section: 'Revenue',
      fields: [
        { name: 'seats', label: 'Seats', format: 'int' },
        { name: 'turns_per_day', label: 'Table Turns', format: 'turns_per_day' },
        { name: 'avg_check', label: 'Avg Check', format: 'currency' },
        { name: 'occupancy_pct', label: 'Occupancy Target', format: 'pct' },
        { name: 'days_open_per_month', label: 'Days Open', format: 'days_per_month' },
        { name: 'food_cost_pct', label: 'Food Cost %', format: 'pct' },
      ],
    },
    {
      section: 'Operating Costs',
      fields: [
        { name: 'labor', label: 'Labor', format: 'currency_per_month' },
        { name: 'utilities', label: 'Utilities', format: 'currency_per_month' },
        { name: 'insurance', label: 'Insurance', format: 'currency_per_month' },
        { name: 'marketing', label: 'Marketing', format: 'currency_per_month' },
        { name: 'loan_payments', label: 'Loan Payments', format: 'currency_per_month' },
      ],
    },
    {
      section: 'Startup Costs',
      fields: [
        { name: 'kitchen_buildout_cost', label: 'Kitchen Build-Out', format: 'currency' },
        { name: 'equipment_cost', label: 'Equipment', format: 'currency' },
        { name: 'preopening_costs', label: 'Pre-Opening', format: 'currency' },
        { name: 'initial_inventory', label: 'Initial Inventory', format: 'currency' },
      ],
    },
    {
      section: 'Capital Structure',
      fields: [
        { name: 'initial_capital_invested', label: 'Initial Investment', format: 'currency' },
        { name: 'loans_payable', label: 'Loans', format: 'currency_or_none', alwaysShow: true },
      ],
    },
    {
      section: 'Growth',
      fields: [
        { name: 'annual_growth_rate', label: 'Annual Growth Rate', format: 'pct' },
      ],
    },
  ],
};

const SERVICES = {
  stages: [
    'classify', 'concept', 'revenue', 'costs', 'opex', 'capital', 'financing',
    'ar_timing', 'growth',
    'bp_collect', 'pfs_collect', 'bp_generate', 'bp_complete',
  ],
  startupLineItems: [
    { key: 'equipment_cost', label: 'Equipment', source: 'collected' },
    { key: 'office_setup_cost', label: 'Office Setup', source: 'collected' },
    { key: 'vehicle_purchase_cost', label: 'Vehicle Purchase', source: 'collected' },
    { key: 'initial_supplies_cost', label: 'Initial Supplies', source: 'collected' },
    { key: 'legal_licensing_cost', label: 'Legal & Licensing', source: 'collected' },
    { key: 'certifications_cost', label: 'Certifications', source: 'collected' },
    { key: 'initial_marketing_cost', label: 'Initial Marketing', source: 'collected' },
    { key: 'security_deposits', label: 'Security Deposits', source: 'collected' },
    { key: 'hard_costs_subtotal', label: 'Hard Costs Subtotal', source: 'plan', rowClass: 'subtotal' },
    { key: 'contingency_buffer', label: 'Contingency Buffer (10%)', source: 'plan' },
    { key: 'working_capital_reserve', label: 'Working Capital Reserve', source: 'plan' },
    { key: 'total_capital_required', label: 'Total Capital Required', source: 'plan', rowClass: 'total' },
  ],
  keyMetrics: [
    { key: 'break_even_revenue_operating', label: 'Break-Even Revenue (Operating)', format: 'currency', source: 'breakeven' },
    { key: 'break_even_revenue_with_debt_service', label: 'Break-Even Revenue (Incl. Debt Service)', format: 'currency', source: 'breakeven' },
    { key: 'utilization_rate', label: 'Utilization Rate', format: 'percent', source: 'collected' },
  ],
  dataPanelSections: [
    {
      section: 'Business Concept',
      fields: [
        { name: 'business_name', label: 'Name', format: 'text' },
        { name: 'trade_type', label: 'Trade Type', format: 'text' },
        { name: 'legal_structure', label: 'Legal Structure', format: 'text' },
        { name: 'business_stage', label: 'Business Stage', format: 'stage' },
        { name: 'pricing_model', label: 'Pricing Model', format: 'text' },
        { name: 'years_in_operation', label: 'Years in Operation', format: 'int' },
      ],
    },
    {
      section: 'Revenue',
      fields: [
        { name: 'num_billable_staff_w2', label: 'Billable Staff', format: 'int' },
        { name: 'avg_bill_rate_w2', label: 'Avg Bill Rate', format: 'currency' },
        { name: 'billable_hours_per_week', label: 'Billable Hours/Week', format: 'int' },
        { name: 'utilization_rate', label: 'Utilization %', format: 'pct' },
        { name: 'avg_engagement_value', label: 'Avg Engagement Value', format: 'currency' },
        { name: 'projects_per_month', label: 'Projects/Month', format: 'int' },
      ],
    },
    {
      section: 'Cost of Services',
      fields: [
        { name: 'staff_loaded_annual_cost', label: 'Staff Loaded Cost (yr)', format: 'currency' },
        { name: 'billable_staff_count', label: 'Billable Staff Count', format: 'int' },
        { name: 'materials_cost_pct', label: 'Materials Cost %', format: 'pct' },
      ],
    },
    {
      section: 'Operating Costs',
      fields: [
        { name: 'monthly_rent', label: 'Monthly Rent', format: 'currency_per_month' },
        { name: 'vehicle_monthly', label: 'Vehicle', format: 'currency_per_month' },
        { name: 'software_monthly', label: 'Software', format: 'currency_per_month' },
        { name: 'marketing_monthly', label: 'Marketing', format: 'currency_per_month' },
        { name: 'professional_services_monthly', label: 'Professional Services', format: 'currency_per_month' },
        { name: 'insurance_professional_annual', label: 'Professional Insurance (yr)', format: 'currency' },
        { name: 'insurance_general_annual', label: 'General Insurance (yr)', format: 'currency' },
        { name: 'insurance_workers_comp_annual', label: 'Workers Comp (yr)', format: 'currency' },
      ],
    },
    {
      section: 'Startup Costs',
      fields: [
        { name: 'equipment_cost', label: 'Equipment', format: 'currency' },
        { name: 'vehicle_purchase_cost', label: 'Vehicle Purchase', format: 'currency' },
        { name: 'working_capital_reserve', label: 'Working Capital', format: 'currency' },
      ],
    },
    {
      section: 'Capital Structure',
      fields: [
        { name: 'equity_contribution', label: 'Equity Contribution', format: 'currency' },
        { name: 'loan_amount', label: 'Loan Amount', format: 'currency_or_none', alwaysShow: true },
      ],
    },
    {
      section: 'AR & Cash Timing',
      fields: [
        { name: 'days_to_invoice', label: 'Days to Invoice', format: 'int' },
        { name: 'days_to_collect', label: 'Days to Collect', format: 'int' },
        { name: 'advance_deposit_pct', label: 'Advance Deposit %', format: 'pct' },
      ],
    },
    {
      section: 'Growth',
      fields: [
        { name: 'annual_growth_rate_y2', label: 'Year 2 Growth', format: 'pct' },
        { name: 'annual_growth_rate_y3', label: 'Year 3 Growth', format: 'pct' },
      ],
    },
  ],
};

const RETAIL = {
  stages: [
    'classify', 'concept', 'revenue', 'cogs', 'opex', 'capital', 'financing', 'growth',
    'bp_collect', 'pfs_collect', 'bp_generate', 'bp_complete',
  ],
  startupLineItems: [
    { key: 'leasehold_improvements', label: 'Leasehold Improvements', source: 'collected' },
    { key: 'fixtures_shelving', label: 'Fixtures & Shelving', source: 'collected' },
    { key: 'signage_cost', label: 'Signage', source: 'collected' },
    { key: 'pos_hardware_cost', label: 'POS Hardware', source: 'collected' },
    { key: 'opening_inventory_capital', label: 'Opening Inventory', source: 'collected' },
    { key: 'security_system_cost', label: 'Security System', source: 'collected' },
    { key: 'pre_opening_marketing', label: 'Pre-Opening Marketing', source: 'collected' },
    { key: 'deposits_capital', label: 'Deposits', source: 'collected' },
    { key: 'legal_licensing_cost', label: 'Legal & Licensing', source: 'collected' },
    { key: 'ecomm_setup_cost', label: 'E-Commerce Setup', source: 'collected' },
    { key: 'hard_costs_subtotal', label: 'Hard Costs Subtotal', source: 'plan', rowClass: 'subtotal' },
    { key: 'contingency_buffer', label: 'Contingency Buffer (10%)', source: 'plan' },
    { key: 'working_capital_reserve', label: 'Working Capital Reserve', source: 'plan' },
    { key: 'total_capital_required', label: 'Total Capital Required', source: 'plan', rowClass: 'total' },
  ],
  keyMetrics: [
    { key: 'break_even_revenue_operating', label: 'Break-Even Revenue (Operating)', format: 'currency', source: 'breakeven' },
    { key: 'break_even_revenue_with_debt_service', label: 'Break-Even Revenue (Incl. Debt Service)', format: 'currency', source: 'breakeven' },
    { key: 'transactions_per_day', label: 'Transactions/Day', format: 'integer', source: 'collected' },
  ],
  dataPanelSections: [
    {
      section: 'Business Concept',
      fields: [
        { name: 'business_name', label: 'Name', format: 'text' },
        { name: 'store_type', label: 'Store Type', format: 'text' },
        { name: 'legal_structure', label: 'Legal Structure', format: 'text' },
        { name: 'business_stage', label: 'Business Stage', format: 'stage' },
        { name: 'sales_channels', label: 'Sales Channels', format: 'text' },
        { name: 'store_count', label: 'Store Count', format: 'int' },
        { name: 'store_sqft', label: 'Store Size', format: 'sqft' },
        { name: 'years_in_operation', label: 'Years in Operation', format: 'int' },
      ],
    },
    {
      section: 'Revenue',
      fields: [
        { name: 'transactions_per_day', label: 'Transactions/Day', format: 'int' },
        { name: 'operating_days_per_week', label: 'Operating Days/Week', format: 'int' },
        { name: 'avg_transaction_value', label: 'Avg Transaction Value', format: 'currency' },
        { name: 'monthly_online_orders', label: 'Online Orders/Month', format: 'int' },
        { name: 'avg_online_order_value', label: 'Avg Online Order Value', format: 'currency' },
        { name: 'customer_return_rate', label: 'Customer Return Rate', format: 'pct' },
        { name: 'traffic_ramp_months', label: 'Traffic Ramp (months)', format: 'int' },
        { name: 'seasonal_peak', label: 'Seasonal Peak', format: 'bool' },
        { name: 'peak_months', label: 'Peak Months', format: 'text' },
      ],
    },
    {
      section: 'Cost of Goods',
      fields: [
        { name: 'gross_margin_pct', label: 'Gross Margin %', format: 'pct' },
        { name: 'shrinkage_pct', label: 'Shrinkage %', format: 'pct' },
        { name: 'payment_processing_rate', label: 'Payment Processing %', format: 'pct' },
        { name: 'supplier_payment_terms', label: 'Supplier Terms', format: 'text' },
        { name: 'inventory_turnover_target', label: 'Inventory Turnover Target', format: 'text' },
        { name: 'supplier_lead_time_days', label: 'Supplier Lead Time (days)', format: 'int' },
        { name: 'opening_inventory_cost', label: 'Opening Inventory Cost', format: 'currency' },
        { name: 'ecomm_shipping_cost_per_order', label: 'E-Comm Shipping/Order', format: 'currency' },
      ],
    },
    {
      section: 'Operating Costs',
      fields: [
        { name: 'monthly_rent', label: 'Monthly Rent', format: 'currency_per_month' },
        { name: 'cam_charges_monthly', label: 'CAM Charges', format: 'currency_per_month' },
        { name: 'utilities_monthly', label: 'Utilities', format: 'currency_per_month' },
        { name: 'pos_software_monthly', label: 'POS Software', format: 'currency_per_month' },
        { name: 'marketing_monthly', label: 'Marketing', format: 'currency_per_month' },
        { name: 'packaging_monthly', label: 'Packaging', format: 'currency_per_month' },
        { name: 'ecomm_platform_monthly', label: 'E-Comm Platform', format: 'currency_per_month' },
        { name: 'ecomm_platform_rev_pct', label: 'E-Comm Platform %', format: 'pct' },
        { name: 'insurance_annual', label: 'Insurance (yr)', format: 'currency' },
        { name: 'owner_annual_draw', label: 'Owner Draw (yr)', format: 'currency' },
        { name: 'owner_hours_per_week', label: 'Owner Hours/Week', format: 'int' },
        { name: 'ft_employee_count', label: 'Full-Time Employees', format: 'int' },
        { name: 'pt_employee_count', label: 'Part-Time Employees', format: 'int' },
        { name: 'avg_hourly_wage', label: 'Avg Hourly Wage', format: 'currency' },
        { name: 'pt_hours_per_week', label: 'PT Hours/Week', format: 'int' },
        { name: 'payroll_burden_pct', label: 'Payroll Burden %', format: 'pct' },
      ],
    },
    {
      section: 'Startup Costs',
      fields: [
        { name: 'leasehold_improvements', label: 'Leasehold Improvements', format: 'currency' },
        { name: 'fixtures_shelving', label: 'Fixtures & Shelving', format: 'currency' },
        { name: 'opening_inventory_capital', label: 'Opening Inventory', format: 'currency' },
        { name: 'working_capital_reserve', label: 'Working Capital', format: 'currency' },
      ],
    },
    {
      section: 'Capital Structure',
      fields: [
        { name: 'equity_contribution', label: 'Equity Contribution', format: 'currency' },
        { name: 'loan_amount', label: 'Loan Amount', format: 'currency_or_none', alwaysShow: true },
      ],
    },
    {
      section: 'Growth',
      fields: [
        { name: 'annual_growth_rate_y2', label: 'Year 2 Growth', format: 'pct' },
        { name: 'annual_growth_rate_y3', label: 'Year 3 Growth', format: 'pct' },
      ],
    },
  ],
};

export const VERTICAL_CONFIG = {
  restaurant: RESTAURANT,
  services: SERVICES,
  retail: RETAIL,
};

// Break-even metrics that only make sense for restaurants (the engines set
// these to 0 for services/retail). Used as a defensive filter so a stray
// restaurant-only metric never renders on a non-restaurant plan.
const RESTAURANT_ONLY_METRICS = new Set([
  'break_even_covers_daily',
  'break_even_occupancy_pct',
]);

export function getVerticalConfig(vertical) {
  return VERTICAL_CONFIG[vertical] || VERTICAL_CONFIG.restaurant;
}

export function getStages(vertical) {
  return getVerticalConfig(vertical).stages;
}

export function isRestaurantOnly(metricKey) {
  return RESTAURANT_ONLY_METRICS.has(metricKey);
}
